import React, { useState, useEffect } from 'react';
import { trackDoctorSelection, trackDirectionsClick, trackWhatsAppClick, getAnalyticsSessionId, createPatientHash } from '../utils/analytics';

// Enhanced Doctor Photo Component with better error handling
const DoctorPhoto = ({ photoUrl, doctorName, doctorId }) => {
  const [imageState, setImageState] = useState('loading'); // 'loading', 'loaded', 'error'
  const [imageSrc, setImageSrc] = useState(null);

  useEffect(() => {
    if (!photoUrl) {
      setImageState('error');
      return;
    }

    setImageState('loading');
    setImageSrc(photoUrl);
    
    // Create a new image element to test loading
    const img = new Image();
    
    const handleLoad = () => {
      // Photo loaded successfully
      setImageState('loaded');
    };
    
    const handleError = () => {
      // Photo loading failed
      setImageState('error');
      
      // Try a fallback approach - sometimes CORS is the issue
      // We can try using a proxy or different approach
      if (photoUrl.includes('http://')) {
        const httpsUrl = photoUrl.replace('http://', 'https://');
        // Retrying with HTTPS

        const retryImg = new Image();
        retryImg.onload = () => {
          // HTTPS retry successful
          setImageSrc(httpsUrl);
          setImageState('loaded');
        };
        retryImg.onerror = () => {
          // HTTPS retry failed
          setImageState('error');
        };
        retryImg.src = httpsUrl;
      }
    };
    
    img.onload = handleLoad;
    img.onerror = handleError;
    
    // Add a timeout for slow loading images
    const timeout = setTimeout(() => {
      if (imageState === 'loading') {
        // Image loading timeout
        setImageState('error');
      }
    }, 10000); // 10 second timeout
    
    img.src = photoUrl;
    
    return () => {
      img.onload = null;
      img.onerror = null;
      clearTimeout(timeout);
    };
  }, [photoUrl, doctorName, doctorId, imageState]);

  if (imageState === 'loading') {
    return (
      <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-gray-400 border-t-gray-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (imageState === 'loaded' && imageSrc) {
    return (
      <img
        src={imageSrc}
        alt={doctorName}
        className="w-full h-full object-cover transition-opacity duration-300"
        onError={() => {
          // Image error after load
          setImageState('error');
        }}
      />
    );
  }

  // Fallback/error state with doctor's initials
  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
      <span className="text-blue-700 font-bold text-lg">
        {getInitials(doctorName)}
      </span>
    </div>
  );
};

// Enhanced WhatsApp Booking Component with better URL handling
const WhatsAppBookingButton = ({ doctor, patientName }) => {
  const generateWhatsAppUrl = () => {
    let phoneNumber = doctor.whatsapp_e164 || doctor.phone_e164 || '';
    
    // Log original phone number for debugging
    // Processing WhatsApp contact
    
    if (!phoneNumber) {
      // No WhatsApp number available
      return null;
    }
    
    // Clean and format phone number
    // Remove all non-digit characters except leading +
    let cleanNumber = phoneNumber.replace(/[^\d+]/g, '');
    
    // Ensure it starts with + for international format
    if (!cleanNumber.startsWith('+')) {
      // If it starts with 91 (India), add +
      if (cleanNumber.startsWith('91')) {
        cleanNumber = '+' + cleanNumber;
      } 
      // If it starts with other country codes or is just a local number
      else if (cleanNumber.length >= 10) {
        // Assume Indian number if 10 digits, otherwise use as-is
        cleanNumber = cleanNumber.length === 10 ? '+91' + cleanNumber : '+' + cleanNumber;
      }
      else {
        // Invalid phone number format
        return null;
      }
    }
    
    // Phone number cleaned for WhatsApp
    
    // Create the message with proper encoding
    const message = `Hello ${doctor.name.startsWith('Dr.') ? doctor.name : `Dr. ${doctor.name}`}!

I would like to book a dermatology appointment.

👤 Patient: ${patientName}
🤖 Referred by: my-skin.ai

Please let me know your available time slots.

Thank you!`;

    // Encode the message properly
    const encodedMessage = encodeURIComponent(message);
    
    // Generate final URL
    const whatsappUrl = `https://wa.me/${cleanNumber.replace('+', '')}?text=${encodedMessage}`;
    
    // WhatsApp URL generated
    
    return whatsappUrl;
  };

  const handleWhatsAppClick = (e) => {
    const url = generateWhatsAppUrl();
    
    if (!url) {
      e.preventDefault();
      alert(`Unable to create WhatsApp link for ${doctor.name.startsWith('Dr.') ? doctor.name : `Dr. ${doctor.name}`}. Phone number may be invalid.`);
      return;
    }
    
    // WhatsApp link clicked - tracking booking attempt
  };

  const whatsappUrl = generateWhatsAppUrl();

  if (!whatsappUrl) {
    return (
      <div className="w-full text-center py-3 px-4 bg-gray-400 text-white rounded-xl text-sm font-semibold cursor-not-allowed flex items-center justify-center space-x-2">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.520-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787"/>
        </svg>
        <span>Contact Info Unavailable</span>
      </div>
    );
  }

  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="w-full text-center py-3 px-4 bg-green-600 text-white rounded-xl text-sm font-semibold transition-all duration-200 hover:bg-green-700 active:bg-green-800 active:scale-95 flex items-center justify-center space-x-2"
      onClick={handleWhatsAppClick}
    >
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787"/>
      </svg>
      <span>Book Now</span>
    </a>
  );
};

const DoctorFinder = ({
  nextStep,
  previousStep,
  goToStep,
  updateSelectedDoctor,
  appState,
  setError,
  setLoading,
  updateUserLocation
}) => {
  const [doctors, setDoctors] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setErrorState] = useState(null);
  const [searchComplete, setSearchComplete] = useState(false);
  const [searchRadius, setSearchRadius] = useState(20);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);

  // Track referral to main site referrals table (for admin visibility)
  const trackMainSiteReferral = async (doctor, interactionType) => {
    try {
      const patientData = appState?.patientData || {};
      const response = await fetch('/api/main-site-referral', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-session-id': sessionStorage.getItem('session_id') || `session_${Date.now()}`
        },
        body: JSON.stringify({
          doctorId: doctor.id,
          doctorName: doctor.name,
          patientName: patientData.name || null,
          patientPhone: patientData.phone || null,
          patientCity: patientData.city || null,
          patientState: patientData.state || null,
          interactionType
        })
      });
      if (!response.ok) {
      } else {
      }
    } catch (error) {
    }
  };

  // Start doctor search when component mounts or show location modal
  useEffect(() => {
    if (appState.userLocation && !searchComplete && !isSearching) {
      findNearbyDoctors();
    } else if (!appState.userLocation) {
      setShowLocationModal(true);
    }
  }, [appState.userLocation]); // Removed searchRadius from dependencies

  // Initialize modal state on component mount
  useEffect(() => {
    if (!appState.userLocation) {
      setShowLocationModal(true);
    }
  }, []);

  const findNearbyDoctors = async () => {
    if (!appState.userLocation) {
      setErrorState('Location permission required');
      return;
    }

    setIsSearching(true);
    setErrorState(null);
    setLoading(true);

    try {
      const { lat, lng } = appState.userLocation;
      
      const API_BASE = process.env.NODE_ENV === 'development' ? '' : '';
      const response = await fetch(
        `${API_BASE}/api/nearby-derms?lat=${lat}&lng=${lng}&limit=12&radius_km=${searchRadius}`,
        { cache: 'no-store' }
      );

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();

      // Processing API response

      if (data.doctors && Array.isArray(data.doctors) && data.doctors.length > 0) {
        // Doctors found in API response
        
        // CLIENT-SIDE RADIUS FILTER
        const filteredDoctors = data.doctors.filter(doctor => {
          const distance = doctor.distance_km || 0;
          // Applying distance filter
          return distance <= searchRadius;
        });

        // Applied client-side radius filter
        
        const sortedDoctors = filteredDoctors.sort((a, b) => 
          (a.distance_km || 0) - (b.distance_km || 0)
        );
        
        setDoctors(sortedDoctors);
        setSearchComplete(true);
        // Final filtered results processed
      } else {
        setDoctors([]);
        setSearchComplete(true);
        // No doctors found within radius
      }
    } catch (err) {
      // Doctor search error occurred
      setErrorState(err.message || 'Failed to find doctors');
      setError(err.message || 'Failed to find doctors');
    } finally {
      setIsSearching(false);
      setLoading(false);
    }
  };

  const retrySearch = () => {
    setDoctors([]);
    setErrorState(null);
    setSearchComplete(false);
    findNearbyDoctors();
  };

  const requestPreciseLocation = async () => {
    setIsRequestingLocation(true);
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          }
        );
      });
      
      const location = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy,
        source: 'precise_gps'
      };
      
      // Update app state with new location
      updateUserLocation && updateUserLocation(location);
      setShowLocationModal(false);
    } catch (error) {
      // Location request failed
      setError('Unable to get precise location. Please try again.');
    } finally {
      setIsRequestingLocation(false);
    }
  };

  const changeSearchRadius = (newRadius) => {
    setSearchRadius(newRadius);
    if (appState.userLocation) {
      setDoctors([]);
      setSearchComplete(false);
      setTimeout(() => findNearbyDoctors(), 100);
    }
  };

  const patientName = appState.patientData?.name || 'Patient';

  // Handle doctor selection and proceed to enhanced booking
  const handleDoctorSelection = async (doctor) => {
    try {
      setLoading(true);

      // Track doctor selection for analytics (HIPAA compliant)
      const patientHash = createPatientHash(appState.patientData);
      const doctorData = {
        id: doctor.id,
        clinic_id: doctor.clinic_id || null
      };

      const trackingResult = await trackDoctorSelection(doctorData, appState.patientData);

      // Track for main site referrals (admin visibility)
      trackMainSiteReferral(doctor, 'doctor_selected');

      // Set selected doctor in app state
      updateSelectedDoctor({
        ...doctor,
        distanceFormatted: typeof doctor.distance_km === 'number' 
          ? `${doctor.distance_km.toFixed(1)} km` 
          : 'Unknown distance',
        clinic_name: doctor.address || 'Clinic Address Not Available'
      });

      // Proceed to enhanced WhatsApp booking component
      nextStep();
    } catch (error) {
      // Doctor selection failed
      setError('Failed to select doctor. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle directions click with tracking
  const handleDirectionsClick = async (doctor) => {
    // Directions clicked for doctor

    try {
      // Simple tracking for directions
      fetch('/api/simple-track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: 'directions_clicked',
          session_id: 'session_' + Date.now(),
          doctor_id: doctor.id,
          meta: {
            doctor_name: doctor.name,
            timestamp: new Date().toISOString()
          }
        })
      }).catch(err => {/* Directions tracking failed */});

      // Track for main site referrals (admin visibility)
      trackMainSiteReferral(doctor, 'directions_clicked');
    } catch (error) {
      // Directions tracking failed
      // Continue with directions even if tracking fails
    }
  };


  // Show loading state
  if (isSearching) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4">
        <div className="max-w-sm w-full text-center">
          <div className="w-12 h-12 mx-auto mb-6 border-2 border-gray-200 border-t-black rounded-full animate-spin"></div>
          <h2 className="text-xl font-bold text-gray-900 mb-3">Finding Doctors</h2>
          <p className="text-gray-600 text-sm">Searching within {searchRadius}km of your location...</p>
        </div>
      </div>
    );
  }

  // Show error or no results
  if (error || (searchComplete && doctors.length === 0)) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4">
        <div className="max-w-sm w-full text-center">
          <div className="w-16 h-16 mx-auto mb-6 bg-yellow-50 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-3">
            {error ? 'Search Failed' : 'No Doctors Found'}
          </h2>
          <p className="text-gray-600 mb-4 text-sm leading-relaxed">
            {error || `No dermatologists found within ${searchRadius}km of your location.`}
          </p>
          {!error && (
            <p className="text-blue-600 mb-8 text-sm">
              Try expanding the search radius to 50km?
            </p>
          )}
          <div className="space-y-3 w-full">
            {!error && searchRadius === 20 && (
              <button 
                onClick={() => changeSearchRadius(50)}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium transition-colors"
              >
                Search within 50km
              </button>
            )}
            <button 
              onClick={retrySearch}
              className="w-full bg-black text-white py-3 px-4 rounded-lg font-medium transition-colors"
            >
              Try Again
            </button>
            <button 
              onClick={() => goToStep(3)}
              className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-medium transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Location Modal
  if (showLocationModal) {
    return (
      <>
        <div className="min-h-screen bg-white">
          <div className="px-4 py-6 border-b border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={previousStep}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="text-center">
                <h1 className="text-lg font-bold text-gray-900">Find Doctors</h1>
              </div>
              <div className="w-9"></div>
            </div>
          </div>
          
          <div className="flex-1 flex items-center justify-center p-4">
            <div className="max-w-sm w-full text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-blue-50 to-purple-50 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Share Your Location</h2>
              <p className="text-gray-600 mb-8 text-sm leading-relaxed">
                We need your precise location to find the best dermatologists near you. Your location is used only for this search and is not permanently stored.
              </p>
              
              <div className="space-y-3">
                <button 
                  onClick={requestPreciseLocation}
                  disabled={isRequestingLocation}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-4 px-4 rounded-xl font-semibold transition-colors flex items-center justify-center space-x-2"
                >
                  {isRequestingLocation ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Getting Location...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span>Allow Location Access</span>
                    </>
                  )}
                </button>
                
                <button 
                  onClick={() => goToStep(3)}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-4 rounded-xl font-medium transition-colors"
                >
                  Go Back
                </button>
              </div>
              
              <div className="mt-6 p-4 bg-green-50 rounded-xl">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <p className="text-green-800 text-sm font-medium mb-1">Privacy Protected</p>
                    <p className="text-green-700 text-xs leading-relaxed">
                      Your location is used only for finding nearby doctors and is never saved or shared.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Check if user location is available after modal
  if (!appState.userLocation) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4">
        <div className="max-w-sm w-full text-center">
          <div className="w-16 h-16 mx-auto mb-6 bg-red-50 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-3">Location Required</h2>
          <p className="text-gray-600 mb-8 text-sm leading-relaxed">
            We need your location to find nearby dermatologists. Please allow location access.
          </p>
          <button 
            onClick={() => setShowLocationModal(true)}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium transition-colors mb-3"
          >
            Try Again
          </button>
          <button 
            onClick={() => goToStep(3)}
            className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-medium transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Show doctors list
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="px-4 py-6 border-b border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => goToStep(3)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="text-center">
            <h1 className="text-lg font-bold text-gray-900">Nearby Doctors</h1>
          </div>
          <div className="w-9"></div>
        </div>
        <div className="text-center">
          <p className="text-sm text-gray-600">
            {doctors.length} doctor{doctors.length !== 1 ? 's' : ''} within {searchRadius}km
          </p>
          <div className="flex items-center justify-center space-x-2 mt-2">
            <button
              onClick={() => changeSearchRadius(20)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                searchRadius === 20 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              20km
            </button>
            <button
              onClick={() => changeSearchRadius(50)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                searchRadius === 50 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              50km
            </button>
          </div>
        </div>
      </div>

      {/* Doctors List */}
      <div className="px-4 py-4">
        <div className="space-y-6 max-w-lg mx-auto">
          {doctors.map((doctor) => (
            <div key={doctor.id} className="bg-white rounded-3xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
              {/* Doctor Info */}
              <div className="flex items-start space-x-6 mb-6">
                <div className="w-28 h-28 bg-gray-200 rounded-2xl overflow-hidden flex-shrink-0 shadow-md">
                  <DoctorPhotoLite photoUrl={doctor.photo_url} doctorName={doctor.name} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-900 text-lg mb-2">
                    {doctor.name.startsWith('Dr.') ? doctor.name : `Dr. ${doctor.name}`}
                  </h3>
                  {doctor.address && (
                    <div className="flex items-start space-x-2 mb-3">
                      <svg className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">
                        {doctor.address}
                      </p>
                    </div>
                  )}
                  {typeof doctor.distance_km === 'number' && (
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <p className="text-sm text-green-700 font-semibold">
                        {doctor.distance_km.toFixed(1)} km away
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                {/* Primary Actions */}
              <div className="flex space-x-3">
                {doctor.maps_url && (
                  <a 
                    href={doctor.maps_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                      onClick={() => handleDirectionsClick(doctor)}
                      className="flex-1 text-center py-3 px-4 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium transition-all duration-200 hover:bg-gray-200 active:scale-95 flex items-center justify-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>Directions</span>
                  </a>
                )}
                <div className="flex-1">
                    <WhatsAppBookingLite doctor={doctor} patientData={appState.patientData} analysisResult={appState.analysisResult} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Info Section */}
        <div className="mt-8 max-w-md mx-auto space-y-4">
          <div className="bg-blue-50 rounded-2xl p-4">
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-blue-900 mb-2">Booking Options</h3>
                <ul className="text-blue-800 text-sm space-y-1">
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">•</span>
                    <span><strong>Book Now</strong> for direct WhatsApp messaging with your AI analysis report</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gray-600 mr-2">•</span>
                    <span><strong>Directions</strong> to navigate to the clinic</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          {/* Simple Usage Notice */}
          <div className="bg-green-50 rounded-xl p-3 border border-green-200">
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-green-800 text-xs">
                Click "Book Now" to send a WhatsApp message to the doctor requesting a consultation appointment
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Actions */}
        <div className="mt-8 pb-8 max-w-md mx-auto space-y-3">
          <button 
            onClick={retrySearch}
            className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-medium transition-colors"
          >
            Refresh List
          </button>
          <button 
            onClick={() => goToStep(3)}
            className="w-full bg-black text-white py-3 px-4 rounded-lg font-medium transition-colors"
          >
            ← Back to Analysis
          </button>
        </div>
      </div>
    </div>
  );
};

export default DoctorFinder;

// Lightweight components used above
function DoctorPhotoLite({ photoUrl, doctorName }) {
  const [src, setSrc] = React.useState('');
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => {
    if (!photoUrl) { setSrc(''); setFailed(true); return; }
    const normalized = photoUrl.startsWith('http://') ? photoUrl.replace('http://','https://') : photoUrl;
    setSrc(normalized);
    setFailed(false);
  }, [photoUrl]);

  const initials = (doctorName || 'DR').split(' ').map(n=>n[0]).join('').toUpperCase().slice(0,2);
  if (!src || failed) {
    return (
      <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
        <span className="text-blue-700 font-bold text-lg">{initials}</span>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={doctorName}
      className="w-full h-full object-cover"
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}

function WhatsAppBookingLite({ doctor, patientData, analysisResult }) {
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showPatientForm, setShowPatientForm] = useState(false);
  const [collectedPatientData, setCollectedPatientData] = useState({
    name: '',
    phone: ''
  });

  const handleBookNowClick = () => {
    // Show patient form first if we don't have patient data
    if (!patientData?.name && !collectedPatientData.name) {
      setShowPatientForm(true);
    } else {
      setShowConsentModal(true);
    }
  };

  const handlePatientFormSubmit = (e) => {
    e.preventDefault();
    if (collectedPatientData.name && collectedPatientData.phone) {
      setShowPatientForm(false);
      setShowConsentModal(true);
    }
  };

  // Use collected data or fallback to passed patientData
  const finalPatientData = collectedPatientData.name ? collectedPatientData : patientData;

  const handleWhatsAppBooking = async () => {
    // REMOVED: Debug logs that exposed doctor contact information
    
    // Step 1: Create HIPAA-compliant referral
    try {
      const referralResponse = await fetch('/api/referral', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patientName: finalPatientData?.name || 'Anonymous',
          patientPhone: finalPatientData?.phone || '',
          doctorId: doctor.id,
          clinicId: doctor.id,
          // analysisResult: removed - we don't store medical analysis
          sessionId: null,
          consentGiven: true
        }),
      });

      if (referralResponse.ok) {
        const referralData = await referralResponse.json();
        // Referral created successfully
      } else {
        // Referral creation failed, continuing with WhatsApp
      }
    } catch (error) {
      // Referral creation error occurred
    }
    
    // Step 2: Generate WhatsApp URL
    let whatsappUrl = '';
    
    if (doctor.whatsapp_e164 && doctor.whatsapp_e164.startsWith('https://wa.me/')) {
      whatsappUrl = doctor.whatsapp_e164;
      // Using WhatsApp URL from database
    } else if (doctor.whatsapp_url && doctor.whatsapp_url.startsWith('https://wa.me/')) {
      whatsappUrl = doctor.whatsapp_url;
      // Using WhatsApp URL from field
    } else {
      // Fallback: create URL from phone number
      let phoneNumber = doctor.phone_e164 || doctor.whatsapp_e164 || doctor.phone;
      
      if (!phoneNumber) {
        alert('Contact information not available for this doctor');
        return;
      }
      
      let cleanPhone = phoneNumber.toString().replace(/[^\d]/g, '');
      if (cleanPhone.length === 10 && !cleanPhone.startsWith('91')) {
        cleanPhone = '91' + cleanPhone;
      }
      
      if (!cleanPhone || cleanPhone.length < 10) {
        // Invalid phone number format
        alert('Unable to create WhatsApp link - invalid phone number format');
        return;
      }
      
      const message = encodeURIComponent(`Hello! I just completed my AI skin analysis on My-skin.ai and would like to schedule a consultation. Please let me know your availability.`);
      whatsappUrl = `https://wa.me/${cleanPhone}?text=${message}`;
      // Created WhatsApp URL from phone
    }
    
    // Step 3: Track the click with REAL patient data (consent given!)
    try {
      // REMOVED: Debug logs that exposed patient PII (HIPAA violation)
      
      const trackingData = {
        action: 'whatsapp_click',
        timestamp: new Date().toISOString(),
        // HIPAA COMPLIANT: NO patient data, NO doctor names, NO phone numbers
        consent_given: true
      };
      
      // REMOVED: Debug log that exposed patient tracking data (HIPAA violation)
      
      fetch('/api/simple-track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: 'whatsapp_clicked',
          session_id: 'session_' + Date.now(),
          doctor_id: doctor.id,
          meta: trackingData
        })
      }).catch(err => {/* Tracking failed */});

      // Track for main site referrals (admin visibility)
      fetch('/api/main-site-referral', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-session-id': sessionStorage.getItem('session_id') || `session_${Date.now()}`
        },
        body: JSON.stringify({
          doctorId: doctor.id,
          doctorName: doctor.name,
          patientName: finalPatientData?.name || null,
          patientPhone: finalPatientData?.phone || null,
          patientCity: finalPatientData?.city || null,
          patientState: finalPatientData?.state || null,
          interactionType: 'whatsapp_clicked'
        })
      }).catch(err => {/* Main site referral tracking failed */});

    } catch (error) {
      // Analytics tracking failed
    }
    
    // Step 4: Open WhatsApp
    // Opening WhatsApp contact
    window.open(whatsappUrl, '_blank');
  };

  const handleConsentGiven = async () => {
    setShowConsentModal(false);
    // Consent given, proceeding with booking
    await handleWhatsAppBooking();
  };

  return (
    <>
      <button
        onClick={handleBookNowClick}
        className="w-full text-center py-3 px-4 bg-green-600 text-white rounded-xl text-sm font-semibold transition-all duration-200 hover:bg-green-700 active:bg-green-800 active:scale-95 flex items-center justify-center space-x-2"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787"/>
        </svg>
      <span>Book Now</span>
      </button>

      {/* Patient Information Form Modal */}
      {showPatientForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 m-4 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Patient Information</h3>
              <button
                onClick={() => setShowPatientForm(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-1">Required for Booking</h4>
                    <p className="text-blue-800 text-sm">
                      We need your contact information to coordinate the appointment with{' '}
                      <strong>{doctor.name.startsWith('Dr.') ? doctor.name : `Dr. ${doctor.name}`}</strong>.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handlePatientFormSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={collectedPatientData.name}
                    onChange={(e) => setCollectedPatientData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    required
                    value={collectedPatientData.phone}
                    onChange={(e) => setCollectedPatientData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="+91 9876543210"
                  />
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600">
                    <strong>Privacy:</strong> We don't store your analysis data/photo. We only store your name and number for the booking process. We store it encrypted and we delete it in 30 days.
                  </p>
                </div>
                
                <div className="flex space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowPatientForm(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!collectedPatientData.name || !collectedPatientData.phone}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Continue
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Consent Modal */}
      {showConsentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 m-4 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Data Sharing Consent</h3>
              <button
                onClick={() => setShowConsentModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-1">HIPAA Compliance Notice</h4>
                    <p className="text-blue-800 text-sm">
                      To book an appointment with <strong>{doctor.name.startsWith('Dr.') ? doctor.name : `Dr. ${doctor.name}`}</strong>,
                      we need your consent to share your basic information with the doctor and my-skin.ai for appointment coordination.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">Information to be shared:</h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-center">
                    <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Your name: <strong>{finalPatientData?.name || 'Not provided'}</strong>
                  </li>
                  <li className="flex items-center">
                    <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Your phone: <strong>{finalPatientData?.phone || 'Not provided'}</strong>
                  </li>
                  <li className="flex items-center">
                    <svg className="w-4 h-4 text-red-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    AI analysis - NOT stored (processed only)
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-600">
                  <strong>Privacy Protection:</strong> We don't store your analysis data/photo. We only store your name and number for the booking process. We store it encrypted and we delete it in 30 days.
                </p>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowConsentModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConsentGiven}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
              >
                I Consent & Book Now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
